import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, X } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import EditorialMarquee from "@/components/EditorialMarquee";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const category = params.get("category") || "All";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api
      .get("/products")
      .then((r) => setPartners(r.data.filter((p) => p.external_url)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (category !== "All") q.category = category;
    if (search.trim()) q.search = search.trim();
    api
      .get("/products", { params: q })
      .then((r) => setProducts(r.data.filter((p) => !p.external_url || p.category === category)))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, search]);

  const selectCategory = (c) => {
    if (c === "All") {
      setParams({});
    } else {
      setParams({ category: c });
    }
  };

  return (
    <main className="pt-24">
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="font-mono text-[11px] tracking-[0.35em] text-[#00ff72]"
        >
          FULL CATALOG — {products.length} COMPOUNDS
        </motion.p>
        <h1 className="mt-3 overflow-hidden font-display text-6xl tracking-tight text-[#F9F6F0] md:text-8xl">
          <motion.span
            className="block"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          >
            The <span className="italic text-[#666666]">Catalog</span>
          </motion.span>
        </h1>
      </section>

      <div className="sticky top-[65px] z-30 border-y border-white/10 bg-black/60 backdrop-blur-xl saturate-150">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2">
            {["All", ...categories.map((c) => c.name).filter((c) => c !== "Supplies")].map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => selectCategory(c)}
                className={`border px-4 py-2 font-mono text-[10px] tracking-[0.2em] transition-colors ${
                  category === c
                    ? "border-[#00ff72] bg-[#00ff72] text-black"
                    : "border-white/15 text-[#A3A3A3] hover:border-white/40 hover:text-[#F9F6F0]"
                }`}
              >
                {c.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="relative md:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#666666]" />
            <input
              data-testid="product-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="SEARCH COMPOUNDS..."
              className="w-full border border-white/15 bg-transparent py-2 pl-10 pr-9 font-mono text-xs tracking-wider text-[#F9F6F0] placeholder:text-[#666666] focus:border-[#00ff72] focus:outline-none"
            />
            {search && (
              <button
                data-testid="product-search-clear"
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666666] hover:text-[#00ff72]"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <section data-testid="catalog-grid" className="mx-auto max-w-7xl px-6 py-16">
        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] animate-pulse border border-white/10 bg-[#111111]" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div data-testid="catalog-empty-state" className="border border-white/10 py-32 text-center">
            <p className="font-display text-3xl text-[#A3A3A3]">No compounds found</p>
            <p className="mt-2 font-mono text-xs tracking-[0.2em] text-[#666666]">
              TRY A DIFFERENT SEARCH OR CATEGORY
            </p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>

      {partners.length > 0 && category === "All" && !search.trim() && (
        <section data-testid="partner-section" className="border-t border-white/10 bg-[#0A0A0A]">
          <div className="mx-auto max-w-7xl px-6 py-20">
            <p className="font-mono text-[11px] tracking-[0.35em] text-[#00ff72]">
              SOLD VIA OUR PARTNER STORES
            </p>
            <h2 className="mb-10 mt-3 font-display text-4xl tracking-tight text-[#F9F6F0] md:text-6xl">
              Ketones <span className="italic text-[#666666]">&amp;</span> Merch
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {partners.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      <EditorialMarquee />
    </main>
  );
}
